'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function CarbohydrateCalculator() {
  const t = useTranslations("tools.carbohydrateCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [unitSystem, setUnitSystem] = useState<'imperial' | 'metric'>('imperial');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [inputs, setInputs] = useState({
    age: 25,
    gender: 'male' as 'male' | 'female',
    feet: 5,
    inches: 10,
    weightLbs: 160,
    heightCm: 178,
    weightKg: 72,
    activity: 1.375,
    goal: 'maintain' as 'maintain' | 'lose' | 'lose-moderate' | 'lose-extreme' | 'gain' | 'gain-moderate',
    carbPercentage: 45,
    formula: 'mifflin' as 'mifflin' | 'harris',
  });
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const { age, gender, feet, inches, weightLbs, heightCm, weightKg, activity, goal, carbPercentage, formula } = inputs;

      if (age < 18 || age > 80) {
        throw new Error(t('errorAge'));
      }

      let heightInCm: number;
      let weightInKg: number;

      if (unitSystem === 'imperial') {
        heightInCm = ((feet * 12) + inches) * 2.54;
        weightInKg = weightLbs * 0.453592;
      } else {
        heightInCm = heightCm;
        weightInKg = weightKg;
      }

      // Calculate BMR
      let bmr: number;
      if (formula === 'mifflin') {
        if (gender === 'male') {
          bmr = (10 * weightInKg) + (6.25 * heightInCm) - (5 * age) + 5;
        } else {
          bmr = (10 * weightInKg) + (6.25 * heightInCm) - (5 * age) - 161;
        }
      } else {
        if (gender === 'male') {
          bmr = 88.362 + (13.397 * weightInKg) + (4.799 * heightInCm) - (5.677 * age);
        } else {
          bmr = 447.593 + (9.247 * weightInKg) + (3.098 * heightInCm) - (4.330 * age);
        }
      }

      // Calculate TDEE
      const tdee = bmr * activity;

      // Adjust for goal
      let targetCalories = tdee;
      switch(goal) {
        case 'lose':
          targetCalories = tdee - 250;
          break;
        case 'lose-moderate':
          targetCalories = tdee - 500;
          break;
        case 'lose-extreme':
          targetCalories = tdee - 1000;
          break;
        case 'gain':
          targetCalories = tdee + 250;
          break;
        case 'gain-moderate':
          targetCalories = tdee + 500;
          break;
      }

      // Calculate macros
      const carbCalories = (targetCalories * carbPercentage) / 100;
      const carbGrams = carbCalories / 4;

      const proteinPercentage = 30;
      const proteinCalories = (targetCalories * proteinPercentage) / 100;
      const proteinGrams = proteinCalories / 4;

      const fatPercentage = 100 - carbPercentage - proteinPercentage;
      const fatCalories = (targetCalories * fatPercentage) / 100;
      const fatGrams = fatCalories / 9;

      setResults({
        carbGrams: Math.round(carbGrams),
        dailyCalories: Math.round(targetCalories),
        carbCalories: Math.round(carbCalories),
        carbPercentage,
        proteinGrams: Math.round(proteinGrams),
        proteinPercentage,
        fatGrams: Math.round(fatGrams),
        fatPercentage,
      });

      // Scroll to results
      setTimeout(() => {
        document.getElementById('results-section')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);

    } catch (err: any) {
      setError(err.message);
    }
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    blue: '#2563eb',
    blueHover: '#1d4ed8',
    green: '#10b981',
    orange: '#f59e0b',
    lightBlue: '#f0f9ff',
    lightGreen: '#f0fdf4',
  };

  const baseStyles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      position: 'relative' as const,
      zIndex: 1,
    },
    header: {
      textAlign: 'center' as const,
      marginBottom: '2rem',
    },
    h1: {
      fontWeight: 700,
      color: colors.textPrimary,
      marginBottom: '1rem',
      lineHeight: 1.2,
    },
    summaryText: {
      lineHeight: 1.6,
      color: colors.textSecondary,
    },
    glassCard: {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    h2: {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '1rem',
    },
    paragraph: {
      lineHeight: 1.6,
      color: colors.textSecondary,
      marginBottom: '1rem',
    },
  };

  const containerStyle = getResponsiveStyle(
    baseStyles.container,
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    baseStyles.header,
    { marginBottom: '1.5rem' },
    { marginBottom: '2rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    baseStyles.h1,
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    baseStyles.summaryText,
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem' },
    { padding: '3rem 2rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    {
      maxWidth: '100%',
      margin: '0 auto',
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden' as const,
    },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = {
    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
    color: 'white',
    padding: '2rem',
    textAlign: 'center' as const,
  };

  const toolHeaderH2Style = getResponsiveStyle(
    {
      margin: 0,
      fontWeight: 700,
      color: 'white',
    },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const formStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const unitTabsStyle = {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '2rem',
    background: '#f1f5f9',
    padding: '0.5rem',
    borderRadius: '0.75rem',
  };

  const unitTabStyle = (isActive: boolean) => ({
    flex: 1,
    padding: '0.75rem',
    border: 'none',
    background: isActive ? 'white' : 'transparent',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    fontWeight: 600,
    color: isActive ? colors.blue : '#64748b',
    boxShadow: isActive ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none',
    transition: 'all 0.3s ease',
  });

  const formGridStyle = getResponsiveStyle(
    {
      display: 'grid',
      gap: '1.5rem',
      marginBottom: '1.5rem',
    },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(2, 1fr)' }
  );

  const formGroupStyle = {
    display: 'flex',
    flexDirection: 'column' as const,
  };

  const labelStyle = {
    fontWeight: 600,
    color: '#334155',
    marginBottom: '0.5rem',
    fontSize: '0.95rem',
  };

  const inputStyle = {
    padding: '0.875rem',
    border: '2px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
    background: 'white',
  };

  const selectStyle = {
    ...inputStyle,
    cursor: 'pointer',
  };

  const heightInputsStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0.75rem',
  };

  const genderOptionsStyle = {
    display: 'flex',
    gap: '1rem',
    marginTop: '0.5rem',
  };

  const genderOptionStyle = {
    flex: 1,
  };

  const genderLabelStyle = (isChecked: boolean) => ({
    display: 'block',
    padding: '0.875rem',
    border: `2px solid ${isChecked ? colors.blue : '#e2e8f0'}`,
    borderRadius: '0.5rem',
    textAlign: 'center' as const,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontWeight: 600,
    background: isChecked ? colors.blue : 'white',
    color: isChecked ? 'white' : colors.textPrimary,
  });

  const advancedSettingsStyle = {
    marginTop: '1.5rem',
    borderTop: '2px solid #f1f5f9',
    paddingTop: '1.5rem',
  };

  const settingsToggleStyle = {
    background: 'none',
    border: 'none',
    color: colors.blue,
    fontWeight: 600,
    cursor: 'pointer',
    padding: 0,
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  };

  const settingsContentStyle = {
    marginTop: '1.5rem',
    display: showAdvanced ? 'block' : 'none',
  };

  const calculateBtnStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.75rem',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1.5rem',
      boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
    },
    { padding: '1rem', fontSize: '1rem' },
    { padding: '1.125rem', fontSize: '1.125rem' },
    { padding: '1.125rem', fontSize: '1.125rem' }
  );

  const errorMessageStyle = {
    background: '#fee2e2',
    borderLeft: '4px solid #ef4444',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
    color: '#991b1b',
    display: error ? 'block' : 'none',
  };

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderTop: '3px solid #10b981',
      display: results ? 'block' : 'none',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    {
      margin: '0 0 1.5rem 0',
      color: '#065f46',
    },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const resultMainStyle = {
    background: 'white',
    padding: '2rem',
    borderRadius: '0.75rem',
    marginBottom: '1.5rem',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
  };

  const resultHighlightStyle = {
    textAlign: 'center' as const,
    marginBottom: '1.5rem',
  };

  const resultLabelStyle = {
    fontSize: '0.95rem',
    color: '#64748b',
    marginBottom: '0.5rem',
  };

  const resultValueStyle = getResponsiveStyle(
    {
      fontWeight: 800,
      color: colors.blue,
      lineHeight: 1,
    },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' },
    { fontSize: '3rem' }
  );

  const resultUnitStyle = {
    fontSize: '1.25rem',
    color: '#64748b',
    marginLeft: '0.5rem',
  };

  const resultGridStyle = getResponsiveStyle(
    {
      display: 'grid',
      gap: '1rem',
    },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(3, 1fr)' }
  );

  const resultItemStyle = {
    background: '#f8fafc',
    padding: '1.25rem',
    borderRadius: '0.5rem',
    borderLeft: `4px solid ${colors.blue}`,
  };

  const resultItemLabelStyle = {
    display: 'block',
    fontSize: '0.875rem',
    color: '#64748b',
    marginBottom: '0.5rem',
    fontWeight: 600,
  };

  const resultItemValueStyle = {
    display: 'block',
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#1e293b',
  };

  const macrosBreakdownStyle = {
    marginTop: '1.5rem',
    background: 'white',
    padding: '1.5rem',
    borderRadius: '0.75rem',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
  };

  const macrosH4Style = {
    margin: '0 0 1rem 0',
    color: '#334155',
    fontSize: '1.125rem',
  };

  const macroBarStyle = {
    marginBottom: '1rem',
  };

  const macroBarHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '0.5rem',
    fontSize: '0.875rem',
  };

  const macroBarFillStyle = {
    height: '8px',
    background: '#e2e8f0',
    borderRadius: '4px',
    overflow: 'hidden' as const,
  };

  const macroBarProgressStyle = (width: number, gradient: string) => ({
    height: '100%',
    background: gradient,
    borderRadius: '4px',
    transition: 'width 0.6s ease',
    width: `${width}%`,
  });

  const infoNoteStyle = {
    background: '#fef3c7',
    borderLeft: '4px solid #f59e0b',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
    fontSize: '0.875rem',
    color: '#78350f',
  };

  const glassCardStyle = getResponsiveStyle(
    baseStyles.glassCard,
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    baseStyles.h2,
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    baseStyles.paragraph,
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = {
    marginLeft: '1.5rem',
    marginBottom: '1rem',
  };

  const listItemStyle = {
    marginBottom: '0.75rem',
    lineHeight: 1.6,
    color: colors.textSecondary,
  };

  const insightCardStyle = {
    background: 'rgba(37, 99, 235, 0.05)',
    borderLeft: `4px solid ${colors.blue}`,
    padding: '1rem',
    borderRadius: '0.5rem',
    marginBottom: '1rem',
  };

  const faqItemStyle = {
    marginBottom: '1.5rem',
  };

  const faqQuestionStyle = {
    fontWeight: 600,
    color: colors.textPrimary,
    marginBottom: '0.5rem',
    fontSize: '1.125rem',
  };

  const referenceStyle = {
    marginBottom: '0.75rem',
    paddingLeft: '1rem',
    borderLeft: `3px solid ${colors.accent}`,
  };

  const referenceLinkStyle = {
    color: colors.blue,
    textDecoration: 'none',
    fontWeight: 500,
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>🥖 {t('toolTitle')}</h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={unitTabsStyle}>
              <button
                type="button"
                style={unitTabStyle(unitSystem === 'imperial')}
                onClick={() => setUnitSystem('imperial')}
              >
                {t('unitImperial')}
              </button>
              <button
                type="button"
                style={unitTabStyle(unitSystem === 'metric')}
                onClick={() => setUnitSystem('metric')}
              >
                {t('unitMetric')}
              </button>
            </div>

            <div style={formGridStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="age">{t('labelAge')}</label>
                <input
                  style={inputStyle}
                  type="number"
                  id="age"
                  min="18"
                  max="80"
                  value={inputs.age}
                  onChange={(e) => setInputs({...inputs, age: parseInt(e.target.value)})}
                  required
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('labelGender')}</label>
                <div style={genderOptionsStyle}>
                  <div style={genderOptionStyle}>
                    <input
                      type="radio"
                      id="male"
                      name="gender"
                      value="male"
                      checked={inputs.gender === 'male'}
                      onChange={(e) => setInputs({...inputs, gender: e.target.value as 'male' | 'female'})}
                      style={{ display: 'none' }}
                    />
                    <label style={genderLabelStyle(inputs.gender === 'male')} htmlFor="male">
                      {t('genderMale')}
                    </label>
                  </div>
                  <div style={genderOptionStyle}>
                    <input
                      type="radio"
                      id="female"
                      name="gender"
                      value="female"
                      checked={inputs.gender === 'female'}
                      onChange={(e) => setInputs({...inputs, gender: e.target.value as 'male' | 'female'})}
                      style={{ display: 'none' }}
                    />
                    <label style={genderLabelStyle(inputs.gender === 'female')} htmlFor="female">
                      {t('genderFemale')}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {unitSystem === 'imperial' ? (
              <div style={formGridStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t('labelHeight')}</label>
                  <div style={heightInputsStyle}>
                    <input
                      style={inputStyle}
                      type="number"
                      min="3"
                      max="8"
                      value={inputs.feet}
                      onChange={(e) => setInputs({...inputs, feet: parseInt(e.target.value)})}
                      placeholder={t('placeholderFeet')}
                    />
                    <input
                      style={inputStyle}
                      type="number"
                      min="0"
                      max="11"
                      value={inputs.inches}
                      onChange={(e) => setInputs({...inputs, inches: parseInt(e.target.value)})}
                      placeholder={t('placeholderInches')}
                    />
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="weight-lbs">{t('labelWeightLbs')}</label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="weight-lbs"
                    min="50"
                    max="500"
                    step="0.1"
                    value={inputs.weightLbs}
                    onChange={(e) => setInputs({...inputs, weightLbs: parseFloat(e.target.value)})}
                  />
                </div>
              </div>
            ) : (
              <div style={formGridStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="height-cm">{t('labelHeightCm')}</label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="height-cm"
                    min="100"
                    max="250"
                    step="0.1"
                    value={inputs.heightCm}
                    onChange={(e) => setInputs({...inputs, heightCm: parseFloat(e.target.value)})}
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="weight-kg">{t('labelWeightKg')}</label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="weight-kg"
                    min="30"
                    max="300"
                    step="0.1"
                    value={inputs.weightKg}
                    onChange={(e) => setInputs({...inputs, weightKg: parseFloat(e.target.value)})}
                  />
                </div>
              </div>
            )}

            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="activity">{t('labelActivity')}</label>
              <select
                style={selectStyle}
                id="activity"
                value={inputs.activity}
                onChange={(e) => setInputs({...inputs, activity: parseFloat(e.target.value)})}
                required
              >
                <option value="1.2">{t('activitySedentary')}</option>
                <option value="1.375">{t('activityLight')}</option>
                <option value="1.465">{t('activityModerate')}</option>
                <option value="1.55">{t('activityActive')}</option>
                <option value="1.725">{t('activityVeryActive')}</option>
                <option value="1.9">{t('activityExtraActive')}</option>
              </select>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="goal">{t('labelGoal')}</label>
              <select
                style={selectStyle}
                id="goal"
                value={inputs.goal}
                onChange={(e) => setInputs({...inputs, goal: e.target.value as any})}
                required
              >
                <option value="maintain">{t('goalMaintain')}</option>
                <option value="lose">{t('goalLoseMild')}</option>
                <option value="lose-moderate">{t('goalLoseModerate')}</option>
                <option value="lose-extreme">{t('goalLoseExtreme')}</option>
                <option value="gain">{t('goalGainMild')}</option>
                <option value="gain-moderate">{t('goalGainModerate')}</option>
              </select>
            </div>

            <div style={advancedSettingsStyle}>
              <button
                type="button"
                style={settingsToggleStyle}
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? '▼' : '▶'}</span> {t('advancedSettings')}
              </button>
              <div style={settingsContentStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="carb-percentage">{t('labelCarbPercentage')}</label>
                  <select
                    style={selectStyle}
                    id="carb-percentage"
                    value={inputs.carbPercentage}
                    onChange={(e) => setInputs({...inputs, carbPercentage: parseInt(e.target.value)})}
                  >
                    <option value="40">{t('carbPercentage40')}</option>
                    <option value="45">{t('carbPercentage45')}</option>
                    <option value="50">{t('carbPercentage50')}</option>
                    <option value="55">{t('carbPercentage55')}</option>
                    <option value="60">{t('carbPercentage60')}</option>
                  </select>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="formula">{t('labelFormula')}</label>
                  <select
                    style={selectStyle}
                    id="formula"
                    value={inputs.formula}
                    onChange={(e) => setInputs({...inputs, formula: e.target.value as 'mifflin' | 'harris'})}
                  >
                    <option value="mifflin">{t('formulaMifflin')}</option>
                    <option value="harris">{t('formulaHarris')}</option>
                  </select>
                </div>
              </div>
            </div>

            {error && (
              <div style={errorMessageStyle}>
                ⚠️ {error}
              </div>
            )}

            <button type="submit" style={calculateBtnStyle}>
              {t('calculateButton')}
            </button>
          </form>

          {results && (
            <div style={resultsStyle} id="results-section">
              <h3 style={resultsH3Style}>📊 {t('resultsTitle')}</h3>

              <div style={resultMainStyle}>
                <div style={resultHighlightStyle}>
                  <div style={resultLabelStyle}>{t('resultDailyTarget')}</div>
                  <div>
                    <span style={resultValueStyle}>{results.carbGrams}</span>
                    <span style={resultUnitStyle}>{t('resultUnitGramsDay')}</span>
                  </div>
                </div>

                <div style={resultGridStyle}>
                  <div style={resultItemStyle}>
                    <span style={resultItemLabelStyle}>{t('resultDailyCalories')}</span>
                    <span style={resultItemValueStyle}>{results.dailyCalories}</span>
                  </div>
                  <div style={resultItemStyle}>
                    <span style={resultItemLabelStyle}>{t('resultCarbCalories')}</span>
                    <span style={resultItemValueStyle}>{results.carbCalories}</span>
                  </div>
                  <div style={resultItemStyle}>
                    <span style={resultItemLabelStyle}>{t('resultPercentage')}</span>
                    <span style={resultItemValueStyle}>{results.carbPercentage}%</span>
                  </div>
                </div>
              </div>

              <div style={macrosBreakdownStyle}>
                <h4 style={macrosH4Style}>{t('macrosTitle')}</h4>

                <div style={macroBarStyle}>
                  <div style={macroBarHeaderStyle}>
                    <span><strong>{t('macroCarbs')}</strong></span>
                    <span>{results.carbGrams}g ({results.carbPercentage}%)</span>
                  </div>
                  <div style={macroBarFillStyle}>
                    <div style={macroBarProgressStyle(results.carbPercentage, 'linear-gradient(90deg, #3b82f6, #2563eb)')}></div>
                  </div>
                </div>

                <div style={macroBarStyle}>
                  <div style={macroBarHeaderStyle}>
                    <span><strong>{t('macroProtein')}</strong></span>
                    <span>{results.proteinGrams}g ({results.proteinPercentage}%)</span>
                  </div>
                  <div style={macroBarFillStyle}>
                    <div style={macroBarProgressStyle(results.proteinPercentage, 'linear-gradient(90deg, #10b981, #059669)')}></div>
                  </div>
                </div>

                <div style={macroBarStyle}>
                  <div style={macroBarHeaderStyle}>
                    <span><strong>{t('macroFat')}</strong></span>
                    <span>{results.fatGrams}g ({results.fatPercentage}%)</span>
                  </div>
                  <div style={macroBarFillStyle}>
                    <div style={macroBarProgressStyle(results.fatPercentage, 'linear-gradient(90deg, #f59e0b, #d97706)')}></div>
                  </div>
                </div>
              </div>

              <div style={infoNoteStyle}>
                <strong>💡 {t('noteTitle')}</strong> {t('noteText')}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('howToUseStep1')}</li>
          <li style={listItemStyle}>{t('howToUseStep2')}</li>
          <li style={listItemStyle}>{t('howToUseStep3')}</li>
          <li style={listItemStyle}>{t('howToUseStep4')}</li>
          <li style={listItemStyle}>{t('howToUseStep5')}</li>
          <li style={listItemStyle}>{t('howToUseStep6')}</li>
        </ol>
      </section>

      {/* Latest Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <div style={insightCardStyle}>
          <p style={paragraphStyle}>{t('insight1')}</p>
        </div>
        <div style={insightCardStyle}>
          <p style={paragraphStyle}>{t('insight2')}</p>
        </div>
        <div style={insightCardStyle}>
          <p style={paragraphStyle}>{t('insight3')}</p>
        </div>
        <div style={insightCardStyle}>
          <p style={paragraphStyle}>{t('insight4')}</p>
        </div>
      </section>

      {/* Best Practices */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('bestPracticesTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('practice1')}</li>
          <li style={listItemStyle}>{t('practice2')}</li>
          <li style={listItemStyle}>{t('practice3')}</li>
          <li style={listItemStyle}>{t('practice4')}</li>
          <li style={listItemStyle}>{t('practice5')}</li>
          <li style={listItemStyle}>{t('practice6')}</li>
        </ul>
      </section>

      {/* Details */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{...h2Style, fontSize: '1.25rem', marginTop: '1.5rem'}}>{t('detailsSubtitle1')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph1')}</p>

        <h3 style={{...h2Style, fontSize: '1.25rem', marginTop: '1.5rem'}}>{t('detailsSubtitle2')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph2')}</p>

        <h3 style={{...h2Style, fontSize: '1.25rem', marginTop: '1.5rem'}}>{t('detailsSubtitle3')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph3')}</p>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq1Question')}</h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq2Question')}</h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq3Question')}</h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq4Question')}</h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq5Question')}</h3>
          <p style={paragraphStyle}>{t('faq5Answer')}</p>
        </div>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <div style={referenceStyle}>
          <a href="https://www.nutrisense.io/blog/best-carb-calculator-apps" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            {t('reference1')}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://www.tryeden.com/calculators/carbs" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            {t('reference2')}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://kefirlab.com/carbohydrate-calculator/" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            {t('reference3')}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://www.calculator.net/carbohydrate-calculator.html" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            {t('reference4')}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://diabetes.org/food-nutrition/understanding-carbs/carb-counting-and-diabetes" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            {t('reference5')}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://www.healthline.com/health/nutrition/macro-calculator" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            {t('reference6')}
          </a>
        </div>
      </section>
    </div>
  );
}